import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VillageOfHopeSignup = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', skills: '', availability: '', message: '',
  });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/volunteer/village-of-hope`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', skills: '', availability: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setSubmitting(false);
  };

  return (
    <section className="py-16 bg-white" id="village-signup" data-testid="village-of-hope-signup">
      <div className="max-w-3xl mx-auto px-6 sm:px-12">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="w-16 h-16 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-[#1E3A8A]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-4">
            Volunteer for Village of Hope
          </h2>
          <p className="text-lg text-slate-600 font-light max-w-xl mx-auto">
            Join our team of dedicated volunteers building a sustainable community
            that provides housing, education, and support for families in need.
          </p>
        </motion.div>

        {status === 'success' ? (
          <motion.div
            className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="village-signup-success"
          >
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-800 mb-2">You're Signed Up!</h3>
            <p className="text-green-700">Thank you for volunteering for Village of Hope. We'll reach out with next steps soon.</p>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            className="bg-slate-50 rounded-2xl p-8 border border-slate-200 space-y-5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                  data-testid="village-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                  data-testid="village-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                <input
                  type="tel" required value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                  data-testid="village-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Availability *</label>
                <select
                  required value={form.availability}
                  onChange={(e) => setForm({ ...form, availability: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                  data-testid="village-availability-select"
                >
                  <option value="">Select availability</option>
                  <option value="weekdays">Weekdays</option>
                  <option value="weekends">Weekends</option>
                  <option value="both">Both Weekdays & Weekends</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Skills / Experience *</label>
              <input
                type="text" required value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                placeholder="e.g., Construction, Teaching, Healthcare, Fundraising..."
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none"
                data-testid="village-skills-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Why do you want to volunteer? *</label>
              <textarea
                required rows={4} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none resize-none"
                data-testid="village-message-textarea"
              />
            </div>

            {status === 'error' && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                Something went wrong. Please try again.
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-4 rounded-xl text-lg"
              data-testid="village-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Sign Up to Volunteer'}
            </Button>
            <p className="text-xs text-slate-400 leading-relaxed mt-4" data-testid="sms-disclosure-village">
              By providing your phone number and submitting this form, you consent to receive SMS messages from
              Prestigious Travel Club Inc. Message frequency may vary. Message and data rates may apply.
              Reply STOP to opt out. Reply HELP for help. Consent is not a condition of purchase.
              See our <a href="/privacy-policy" className="underline hover:text-[#1E3A8A]">Privacy Policy</a> and <a href="/terms-and-conditions" className="underline hover:text-[#1E3A8A]">Terms & Conditions</a>.
            </p>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default VillageOfHopeSignup;
